import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const AddAssetLayer = () => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        item_name: '',
        description: '',
        tagging_number: '',
        asset_category: '',
        serial_number: '',
        location: '',
        department: '',
        holder_user: '',
        useful_life: '',
        date_acquired: '',
        original_cost: '',
        net_book_value: '',
        running_date: '',
        retirement_date: '',
        accumulated_depreciation: '',
        current_month_depreciation: '',
        yearly_depreciation: '',
        full_depreciation: '',
        additions: '',
        disposals: '',
        condition: 'good',
    });
    const [picture, setPicture] = useState(null);
    const [picturePreview, setPicturePreview] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});

    const handlePictureChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPicture(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPicturePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear validation error for this field
        if (validationErrors[name]) {
            setValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setValidationErrors({});

        // Validate all required fields
        const errors = {};
        
        if (!formData.item_name.trim()) {
            errors.item_name = ['Item name is required'];
        }
        
        if (!formData.description.trim()) {
            errors.description = ['Description is required'];
        }
        
        if (!formData.tagging_number.trim()) {
            errors.tagging_number = ['Tagging number is required'];
        }
        
        if (!formData.asset_category) {
            errors.asset_category = ['Asset category is required'];
        }
        
        if (!formData.serial_number.trim()) {
            errors.serial_number = ['Serial number is required'];
        }
        
        if (!formData.location.trim()) {
            errors.location = ['Location is required'];
        }
        
        if (!formData.department.trim()) {
            errors.department = ['Department is required'];
        }
        
        if (!formData.holder_user.trim()) {
            errors.holder_user = ['Holder/User is required'];
        }
        
        if (!formData.useful_life.trim()) {
            errors.useful_life = ['Useful life is required'];
        }
        
        if (!formData.date_acquired) {
            errors.date_acquired = ['Date acquired is required'];
        }
        
        if (!formData.original_cost.trim()) {
            errors.original_cost = ['Original cost is required'];
        }
        
        if (!formData.net_book_value.trim()) {
            errors.net_book_value = ['Net book value is required'];
        }
        
        if (!formData.running_date) {
            errors.running_date = ['Running date is required'];
        }
        
        if (!formData.retirement_date) {
            errors.retirement_date = ['Retirement date is required'];
        }
        
        if (!formData.accumulated_depreciation.trim()) {
            errors.accumulated_depreciation = ['Accumulated depreciation is required'];
        }
        
        if (!formData.current_month_depreciation.trim()) {
            errors.current_month_depreciation = ['Current month depreciation is required'];
        }
        
        if (!formData.yearly_depreciation.trim()) {
            errors.yearly_depreciation = ['Yearly depreciation is required'];
        }
        
        if (!formData.full_depreciation.trim()) {
            errors.full_depreciation = ['Full depreciation is required'];
        }
        
        if (!formData.additions.trim()) {
            errors.additions = ['Additions is required'];
        }
        
        if (!formData.disposals.trim()) {
            errors.disposals = ['Disposals is required'];
        }
        
        if (!formData.condition) {
            errors.condition = ['Condition is required'];
        }

        // If there are validation errors, show them and don't submit
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            setError('Please fill in all required fields');
            return;
        }

        const apiUrl = process.env.REACT_APP_API_URL;
        const token = localStorage.getItem('authToken');

        if (!apiUrl || !token) {
            setError('Missing API URL or authentication token');
            return;
        }

        try {
            setIsSubmitting(true);

            // Prepare form data for multipart/form-data submission (for picture upload)
            const formDataToSend = new FormData();
            
            // Append all form fields
            Object.keys(formData).forEach(key => {
                if (formData[key]) {
                    formDataToSend.append(key, formData[key]);
                }
            });

            // Append picture if exists
            if (picture) {
                formDataToSend.append('picture', picture);
            }

            const response = await fetch(`${apiUrl}/assets`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    // Don't set Content-Type header - browser will set it automatically with boundary
                },
                body: formDataToSend,
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.errors) {
                    setValidationErrors(data.errors);
                }
                throw new Error(data.message || 'Failed to create asset');
            }

            if (data.success) {
                navigate('/asset-list');
            } else {
                throw new Error(data.message || 'Failed to create asset');
            }
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate('/asset-list');
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body p-24">
                <div className="row justify-content-center">
                    <div className="col-12">
                        <div className="card border">
                            <div className="card-body">
                                <h5 className="mb-24">Add New Asset</h5>
                              
                                {error && (
                                    <div className="alert alert-danger mb-20" role="alert">
                                        {error}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} noValidate>
                                    <div className="row">
                                        {/* Item Name */}
                                        <div className="col-md-6 mb-20">
                                        <label
                                                htmlFor="item_name"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                                Item Name <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control radius-8 ${
                                                    validationErrors.item_name ? 'is-invalid' : ''
                                                }`}
                                                id="item_name"
                                                name="item_name"
                                                placeholder="Enter item name"
                                                value={formData.item_name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                            {validationErrors.item_name && (
                                            <div className="invalid-feedback">
                                                    {validationErrors.item_name[0]}
                                            </div>
                                        )}
                                    </div>

                                        {/* Tagging Number */}
                                        <div className="col-md-6 mb-20">
                                        <label
                                                htmlFor="tagging_number"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                                Tagging Number <span className="text-danger-600">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control radius-8 ${
                                                    validationErrors.tagging_number ? 'is-invalid' : ''
                                                }`}
                                                id="tagging_number"
                                                name="tagging_number"
                                                placeholder="Enter tagging number"
                                                value={formData.tagging_number}
                                            onChange={handleInputChange}
                                            required
                                        />
                                            {validationErrors.tagging_number && (
                                            <div className="invalid-feedback">
                                                    {validationErrors.tagging_number[0]}
                                            </div>
                                        )}
                                    </div>

                                        {/* Description */}
                                        <div className="col-12 mb-20">
                                        <label
                                                htmlFor="description"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                                Description
                                        </label>
                                            <textarea
                                            className={`form-control radius-8 ${
                                                    validationErrors.description ? 'is-invalid' : ''
                                                }`}
                                                id="description"
                                                name="description"
                                                placeholder="Enter description"
                                                rows="3"
                                                value={formData.description}
                                            onChange={handleInputChange}
                                        />
                                            {validationErrors.description && (
                                            <div className="invalid-feedback">
                                                    {validationErrors.description[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Picture Upload */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="picture"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Picture
                                            </label>
                                            <input
                                                type="file"
                                                className="form-control radius-8"
                                                id="picture"
                                                accept="image/*"
                                                onChange={handlePictureChange}
                                            />
                                            {picturePreview && (
                                                <div className="mt-2">
                                                    <img
                                                        src={picturePreview}
                                                        alt="Preview"
                                                        className="img-thumbnail"
                                                        style={{ maxWidth: '200px', maxHeight: '200px' }}
                                                    />
                                            </div>
                                        )}
                                    </div>

                                        {/* Asset Category */}
                                        <div className="col-md-6 mb-20">
                                        <label
                                                htmlFor="asset_category"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                                Asset Category <span className="text-danger-600">*</span>
                                        </label>
                                        <select
                                            className={`form-control radius-8 form-select ${
                                                    validationErrors.asset_category ? 'is-invalid' : ''
                                                }`}
                                                id="asset_category"
                                                name="asset_category"
                                                value={formData.asset_category}
                                            onChange={handleInputChange}
                                            required
                                        >
                                                <option value="">Select Asset Category</option>
                                                <option value="Vehicles">Vehicles</option>
                                                <option value="Equipments">Equipments</option>
                                                <option value="Furniture and fixture">Furniture and fixture</option>
                                        </select>
                                            {validationErrors.asset_category && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.asset_category[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Serial Number */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="serial_number"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Serial Number
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control radius-8 ${
                                                    validationErrors.serial_number ? 'is-invalid' : ''
                                                }`}
                                                id="serial_number"
                                                name="serial_number"
                                                placeholder="Enter serial number"
                                                value={formData.serial_number}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.serial_number && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.serial_number[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Location */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="location"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Location
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control radius-8 ${
                                                    validationErrors.location ? 'is-invalid' : ''
                                                }`}
                                                id="location"
                                                name="location"
                                                placeholder="Enter location"
                                                value={formData.location}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.location && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.location[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Department */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="department"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Department
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control radius-8 ${
                                                    validationErrors.department ? 'is-invalid' : ''
                                                }`}
                                                id="department"
                                                name="department"
                                                placeholder="Enter department"
                                                value={formData.department}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.department && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.department[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Holder/User */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="holder_user"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Holder / User
                                            </label>
                                            <input
                                                type="text"
                                                className={`form-control radius-8 ${
                                                    validationErrors.holder_user ? 'is-invalid' : ''
                                                }`}
                                                id="holder_user"
                                                name="holder_user"
                                                placeholder="Enter holder/user"
                                                value={formData.holder_user}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.holder_user && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.holder_user[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Useful Life */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="useful_life"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Useful Life (Years)
                                            </label>
                                            <input
                                                type="number"
                                                className={`form-control radius-8 ${
                                                    validationErrors.useful_life ? 'is-invalid' : ''
                                                }`}
                                                id="useful_life"
                                                name="useful_life"
                                                placeholder="Enter useful life"
                                                value={formData.useful_life}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.useful_life && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.useful_life[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Date Acquired */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="date_acquired"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Date Acquired
                                            </label>
                                            <input
                                                type="date"
                                                className={`form-control radius-8 ${
                                                    validationErrors.date_acquired ? 'is-invalid' : ''
                                                }`}
                                                id="date_acquired"
                                                name="date_acquired"
                                                value={formData.date_acquired}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.date_acquired && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.date_acquired[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Original Cost */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="original_cost"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Original Cost
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className={`form-control radius-8 ${
                                                    validationErrors.original_cost ? 'is-invalid' : ''
                                                }`}
                                                id="original_cost"
                                                name="original_cost"
                                                placeholder="Enter original cost"
                                                value={formData.original_cost}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.original_cost && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.original_cost[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Net Book Value */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="net_book_value"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Net Book Value
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className={`form-control radius-8 ${
                                                    validationErrors.net_book_value ? 'is-invalid' : ''
                                                }`}
                                                id="net_book_value"
                                                name="net_book_value"
                                                placeholder="Enter net book value"
                                                value={formData.net_book_value}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.net_book_value && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.net_book_value[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Running Date */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="running_date"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Running Date
                                            </label>
                                            <input
                                                type="date"
                                                className={`form-control radius-8 ${
                                                    validationErrors.running_date ? 'is-invalid' : ''
                                                }`}
                                                id="running_date"
                                                name="running_date"
                                                value={formData.running_date}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.running_date && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.running_date[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Retirement Date */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="retirement_date"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Retirement Date
                                            </label>
                                            <input
                                                type="date"
                                                className={`form-control radius-8 ${
                                                    validationErrors.retirement_date ? 'is-invalid' : ''
                                                }`}
                                                id="retirement_date"
                                                name="retirement_date"
                                                value={formData.retirement_date}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.retirement_date && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.retirement_date[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Accumulated Depreciation */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="accumulated_depreciation"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Accumulated Depreciation
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className={`form-control radius-8 ${
                                                    validationErrors.accumulated_depreciation ? 'is-invalid' : ''
                                                }`}
                                                id="accumulated_depreciation"
                                                name="accumulated_depreciation"
                                                placeholder="Enter accumulated depreciation"
                                                value={formData.accumulated_depreciation}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.accumulated_depreciation && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.accumulated_depreciation[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Current Month's Depreciation */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="current_month_depreciation"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Current Month's Depreciation
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className={`form-control radius-8 ${
                                                    validationErrors.current_month_depreciation ? 'is-invalid' : ''
                                                }`}
                                                id="current_month_depreciation"
                                                name="current_month_depreciation"
                                                placeholder="Enter current month depreciation"
                                                value={formData.current_month_depreciation}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.current_month_depreciation && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.current_month_depreciation[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Yearly Depreciation */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="yearly_depreciation"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Yearly Depreciation
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className={`form-control radius-8 ${
                                                    validationErrors.yearly_depreciation ? 'is-invalid' : ''
                                                }`}
                                                id="yearly_depreciation"
                                                name="yearly_depreciation"
                                                placeholder="Enter yearly depreciation"
                                                value={formData.yearly_depreciation}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.yearly_depreciation && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.yearly_depreciation[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Full Depreciation */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="full_depreciation"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Full Depreciation
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className={`form-control radius-8 ${
                                                    validationErrors.full_depreciation ? 'is-invalid' : ''
                                                }`}
                                                id="full_depreciation"
                                                name="full_depreciation"
                                                placeholder="Enter full depreciation"
                                                value={formData.full_depreciation}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.full_depreciation && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.full_depreciation[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Additions */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="additions"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Additions
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className={`form-control radius-8 ${
                                                    validationErrors.additions ? 'is-invalid' : ''
                                                }`}
                                                id="additions"
                                                name="additions"
                                                placeholder="Enter additions"
                                                value={formData.additions}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.additions && (
                                                <div className="invalid-feedback">
                                                    {validationErrors.additions[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Disposals */}
                                        <div className="col-md-6 mb-20">
                                            <label
                                                htmlFor="disposals"
                                                className="form-label fw-semibold text-primary-light text-sm mb-8"
                                            >
                                                Disposals
                                            </label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                className={`form-control radius-8 ${
                                                    validationErrors.disposals ? 'is-invalid' : ''
                                                }`}
                                                id="disposals"
                                                name="disposals"
                                                placeholder="Enter disposals"
                                                value={formData.disposals}
                                                onChange={handleInputChange}
                                            />
                                            {validationErrors.disposals && (
                                            <div className="invalid-feedback">
                                                    {validationErrors.disposals[0]}
                                            </div>
                                        )}
                                    </div>

                                        {/* Condition */}
                                        <div className="col-md-6 mb-20">
                                        <label
                                                htmlFor="condition"
                                            className="form-label fw-semibold text-primary-light text-sm mb-8"
                                        >
                                                Condition
                                        </label>
                                        <select
                                            className={`form-control radius-8 form-select ${
                                                    validationErrors.condition ? 'is-invalid' : ''
                                                }`}
                                                id="condition"
                                                name="condition"
                                                value={formData.condition}
                                            onChange={handleInputChange}
                                            >
                                                <option value="excellent">Excellent</option>
                                                <option value="good">Good</option>
                                                <option value="fair">Fair</option>
                                                <option value="poor">Poor</option>
                                                <option value="damaged">Damaged</option>
                                        </select>
                                            {validationErrors.condition && (
                                            <div className="invalid-feedback">
                                                    {validationErrors.condition[0]}
                                            </div>
                                        )}
                                        </div>
                                    </div>

                                    <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                                        <button
                                            type="button"
                                            onClick={handleCancel}
                                            className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                                            disabled={isSubmitting}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? 'Saving...' : 'Save Asset'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddAssetLayer;
